/**
 * Storage Service using Capacitor Preferences API
 * Provides secure storage that works across web, iOS, and Android
 *
 * - Web: Uses localStorage with Preferences API wrapper
 * - iOS: Stores in iOS Keychain
 * - Android: Stores in Android SharedPreferences (encrypted on modern devices)
 */

import { Preferences } from '@capacitor/preferences';

/**
 * Returns true when the error is a storage quota exceeded error.
 * Capacitor wraps localStorage so the error surfaces as a DOMException
 * with name QuotaExceededError (code 22) or as a generic Error whose message
 * mentions quota (Android WebView variant).
 */
export function isQuotaError(error: unknown): boolean {
  if (error instanceof DOMException) {
    return error.name === 'QuotaExceededError' || error.code === 22;
  }
  if (error instanceof Error) {
    return /quota/i.test(error.message);
  }
  return false;
}

const KEYS = {
  FAMILIAR_NAME: 'familiarName',
  ALLOWED_NAME: 'allowedName',
  PREFERRED_PRONOUNS: 'preferredPronouns',
  EMAIL: 'email',
  PENDING_EMAIL: 'pendingEmail', // A not-yet-verified email change, awaiting verification
  PHONE: 'phone', // Full phone number for UI prepopulation
  PENDING_PHONE: 'pendingPhone', // A not-yet-verified phone change, awaiting verification
  ADDRESS_BOOK: 'addressBook', // Address Book contact list
  ADDRESS_BOOK_SYNC: 'addressBookSync', // Server-sync bookkeeping for the address book (#476)
  PENDING_DUPLICATES: 'pendingDuplicates', // Imported duplicates awaiting the Merge duplicates prompt
  USER_SETTINGS: 'userSettings', // User settings including contact saving policy
  DEVICE_ID: 'deviceId',
  TAB_IDS: 'snowball-tab-ids',
  OUTBOX: 'outbox', // Queued messages and where each stands (modules/outbox)
  LAST_TOPIC_TYPE: 'lastTopicType', // Type last picked in the New Topic type picker
} as const;

export const SUPPRESSIBLE_NOTICE_KEYS = {
  contacts: 'interstitial_contacts_suppressed',
  notifications: 'interstitial_notifications_suppressed',
  phoneLinking: 'interstitial_phoneLinking_suppressed',
  homeInstall: 'interstitial_homeInstall_suppressed',
} as const;

export type NoticeKey = keyof typeof SUPPRESSIBLE_NOTICE_KEYS;

// Keys preserved when clearing non-address-book data during a partial reset.
// The sync bookkeeping rides along with the address book so a partial reset
// doesn't make the retained local book look un-synced and force a needless push.
const PRESERVE_ON_RESET = new Set<string>([
  KEYS.ADDRESS_BOOK,
  KEYS.ADDRESS_BOOK_SYNC,
  KEYS.PENDING_DUPLICATES,
]);

export const StorageService = {
  /**
   * Clear all stored data
   */
  async clearAll(): Promise<void> {
    await Preferences.clear();
  },

  /**
   * Store the user's familiar name (used to identify themselves in an SMS to a
   * close contact).
   */
  async setFamiliarName(familiarName: string): Promise<void> {
    await Preferences.set({
      key: KEYS.FAMILIAR_NAME,
      value: familiarName,
    });
  },

  /**
   * Retrieve the user's familiar name
   * @returns The familiar name or undefined if not set
   */
  async getFamiliarName(): Promise<string | undefined> {
    const { value } = await Preferences.get({ key: KEYS.FAMILIAR_NAME });
    return value ?? undefined;
  },

  /**
   * Clear the user's familiar name
   */
  async clearFamiliarName(): Promise<void> {
    await Preferences.remove({ key: KEYS.FAMILIAR_NAME });
  },

  /**
   * Store the user's allowed name (a short name or initials used to attribute an
   * earlier message from them).
   */
  async setAllowedName(allowedName: string): Promise<void> {
    await Preferences.set({
      key: KEYS.ALLOWED_NAME,
      value: allowedName,
    });
  },

  /**
   * Retrieve the user's allowed name
   * @returns The allowed name or undefined if not set
   */
  async getAllowedName(): Promise<string | undefined> {
    const { value } = await Preferences.get({ key: KEYS.ALLOWED_NAME });
    return value ?? undefined;
  },

  /**
   * Clear the user's allowed name
   */
  async clearAllowedName(): Promise<void> {
    await Preferences.remove({ key: KEYS.ALLOWED_NAME });
  },

  /**
   * Store the user's preferred pronouns (e.g. "they/them"), used when others
   * pass their statement along.
   */
  async setPreferredPronouns(preferredPronouns: string): Promise<void> {
    await Preferences.set({
      key: KEYS.PREFERRED_PRONOUNS,
      value: preferredPronouns,
    });
  },

  /**
   * Retrieve the user's preferred pronouns
   * @returns The preferred pronouns or undefined if not set
   */
  async getPreferredPronouns(): Promise<string | undefined> {
    const { value } = await Preferences.get({ key: KEYS.PREFERRED_PRONOUNS });
    return value ?? undefined;
  },

  /**
   * Clear the user's preferred pronouns
   */
  async clearPreferredPronouns(): Promise<void> {
    await Preferences.remove({ key: KEYS.PREFERRED_PRONOUNS });
  },

  /**
   * Store the user's email
   */
  async setEmail(email: string): Promise<void> {
    await Preferences.set({
      key: KEYS.EMAIL,
      value: email,
    });
  },

  /**
   * Retrieve the user's email
   * @returns The email or null if not set
   */
  async getEmail(): Promise<string | undefined> {
    const { value } = await Preferences.get({ key: KEYS.EMAIL });
    return value ?? undefined;
  },

  /**
   * Clear the user's email
   */
  async clearEmail(): Promise<void> {
    await Preferences.remove({ key: KEYS.EMAIL });
  },

  /**
   * Stash a not-yet-verified (pending) email change. It is kept separate
   * from the committed EMAIL key so a reload mid-verification restores the prior
   * address; it is promoted to EMAIL once the change is verified (e.g. after the
   * email magic link), or dropped if the change is abandoned.
   */
  async setPendingEmail(email: string): Promise<void> {
    await Preferences.set({ key: KEYS.PENDING_EMAIL, value: email });
  },

  /**
   * Retrieve the not-yet-verified (pending) email change, if any.
   */
  async getPendingEmail(): Promise<string | undefined> {
    const { value } = await Preferences.get({ key: KEYS.PENDING_EMAIL });
    return value ?? undefined;
  },

  /**
   * Clear the not-yet-verified (pending) email change.
   */
  async clearPendingEmail(): Promise<void> {
    await Preferences.remove({ key: KEYS.PENDING_EMAIL });
  },

  /**
   * Store the user's full phone number (for UI prepopulation)
   */
  async setPhone(phone: string): Promise<void> {
    await Preferences.set({
      key: KEYS.PHONE,
      value: phone,
    });
  },

  /**
   * Retrieve the user's full phone number
   * @returns The phone number or null if not set
   */
  async getPhone(): Promise<string | undefined> {
    const { value } = await Preferences.get({ key: KEYS.PHONE });
    return value ?? undefined;
  },

  /**
   * Clear the user's phone number
   */
  async clearPhone(): Promise<void> {
    await Preferences.remove({ key: KEYS.PHONE });
  },

  /**
   * Stash a not-yet-verified (pending) phone-number change. It is kept
   * separate from the committed PHONE key so a reload mid-verification restores
   * the prior number; it is promoted to PHONE once the claims show the change was
   * verified (e.g. after the SMS magic link), or dropped if the change is
   * abandoned.
   */
  async setPendingPhone(phone: string): Promise<void> {
    await Preferences.set({ key: KEYS.PENDING_PHONE, value: phone });
  },

  /**
   * Retrieve the not-yet-verified (pending) phone-number change, if any.
   */
  async getPendingPhone(): Promise<string | undefined> {
    const { value } = await Preferences.get({ key: KEYS.PENDING_PHONE });
    return value ?? undefined;
  },

  /**
   * Clear the not-yet-verified (pending) phone-number change.
   */
  async clearPendingPhone(): Promise<void> {
    await Preferences.remove({ key: KEYS.PENDING_PHONE });
  },

  /**
   * Get saved Address Book contact list from device storage
   * @returns JSON string representation of contact list or null
   */
  async getAddressBook(): Promise<string | null> {
    try {
      const { value } = await Preferences.get({ key: KEYS.ADDRESS_BOOK });
      return value ?? null;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to get Address Book:', error);
      return null;
    }
  },

  /**
   * Save Address Book contact list to device storage
   * @param json - JSON string representation of contact list
   */
  async setAddressBook(json: string): Promise<void> {
    try {
      await Preferences.set({
        key: KEYS.ADDRESS_BOOK,
        value: json,
      });
    } catch (error) {
      if (isQuotaError(error)) throw error;
      // eslint-disable-next-line no-console
      console.error('Failed to set Address Book:', error);
    }
  },

  /**
   * Clear Address Book from storage
   */
  async clearAddressBook(): Promise<void> {
    try {
      await Preferences.remove({ key: KEYS.ADDRESS_BOOK });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to clear Address Book:', error);
    }
  },

  /**
   * Get the address-book server-sync bookkeeping (#476): the last server version
   * this device synced to, that version's server timestamp (for the divergence
   * date), and a content hash of the last blob pushed (to detect un-synced local
   * changes). Returns null when this device has never synced.
   */
  async getAddressBookSync(): Promise<string | null> {
    try {
      const { value } = await Preferences.get({
        key: KEYS.ADDRESS_BOOK_SYNC,
      });
      return value ?? null;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to get Address Book sync state:', error);
      return null;
    }
  },

  /**
   * Save the address-book server-sync bookkeeping.
   * @param json - JSON string representation of the sync state
   */
  async setAddressBookSync(json: string): Promise<void> {
    try {
      await Preferences.set({ key: KEYS.ADDRESS_BOOK_SYNC, value: json });
    } catch (error) {
      if (isQuotaError(error)) throw error;
      // eslint-disable-next-line no-console
      console.error('Failed to set Address Book sync state:', error);
    }
  },

  /**
   * Get the imported duplicates still waiting for the Merge duplicates prompt,
   * which stay out of the address book until they are merged or dismissed.
   * @returns JSON string representation of the duplicate groups or null
   */
  async getPendingDuplicates(): Promise<string | null> {
    try {
      const { value } = await Preferences.get({
        key: KEYS.PENDING_DUPLICATES,
      });
      return value ?? null;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to get pending duplicates:', error);
      return null;
    }
  },

  /**
   * Save the imported duplicates waiting for the Merge duplicates prompt.
   * @param json - JSON string representation of the duplicate groups
   */
  async setPendingDuplicates(json: string): Promise<void> {
    try {
      await Preferences.set({ key: KEYS.PENDING_DUPLICATES, value: json });
    } catch (error) {
      if (isQuotaError(error)) throw error;
      // eslint-disable-next-line no-console
      console.error('Failed to set pending duplicates:', error);
    }
  },

  /**
   * Get user settings from device storage
   * @returns JSON string representation of user settings or null
   */
  async getUserSettings(): Promise<string | null> {
    try {
      const { value } = await Preferences.get({ key: KEYS.USER_SETTINGS });
      return value ?? null;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to get user settings:', error);
      return null;
    }
  },

  /**
   * Save user settings to device storage
   * @param json - JSON string representation of user settings
   */
  async setUserSettings(json: string): Promise<void> {
    try {
      await Preferences.set({
        key: KEYS.USER_SETTINGS,
        value: json,
      });
    } catch (error) {
      if (isQuotaError(error)) throw error;
      // eslint-disable-next-line no-console
      console.error('Failed to set user settings:', error);
    }
  },

  /**
   * Get last reminder timestamp for a reminder (by reminder ID)
   * @param reminderId - Unique reminder identifier
   * @returns Unix timestamp in milliseconds or null
   */
  async getLastReminderTimestamp(reminderId: string): Promise<number | null> {
    try {
      const key = `reminder_${reminderId}`;
      const { value } = await Preferences.get({ key });
      return value ? parseInt(value, 10) : null;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to get last reminder timestamp:', error);
      return null;
    }
  },

  /**
   * Set last reminder timestamp for a reminder (by reminder ID)
   * @param reminderId - Unique reminder identifier
   */
  async setLastReminderTimestamp(reminderId: string): Promise<void> {
    try {
      const key = `reminder_${reminderId}`;
      await Preferences.set({ key, value: Date.now().toString() });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to set last reminder timestamp:', error);
    }
  },

  /**
   * Get the outbox: every queued share and where each of its messages stands.
   * @returns JSON string or null if nothing has been queued
   */
  async getOutbox(): Promise<string | null> {
    try {
      const { value } = await Preferences.get({ key: KEYS.OUTBOX });
      return value ?? null;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to get outbox:', error);
      return null;
    }
  },

  /**
   * Save the outbox
   * @param json - JSON string of the stored outbox (see modules/outbox)
   */
  async setOutbox(json: string): Promise<void> {
    try {
      await Preferences.set({ key: KEYS.OUTBOX, value: json });
    } catch (error) {
      if (isQuotaError(error)) throw error;
      // eslint-disable-next-line no-console
      console.error('Failed to set outbox:', error);
    }
  },

  /**
   * Get the saved state of the share wizard for a topic
   * @param topicId - Topic identifier
   * @returns JSON string or null if not set
   */
  async getShareWizardState(topicId: string): Promise<string | null> {
    try {
      const { value } = await Preferences.get({
        key: `share_wizard_${topicId}`,
      });
      return value ?? null;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to get sharing wizard state:', error);
      return null;
    }
  },

  /**
   * Save share wizard leg state for a topic
   * @param topicId - Topic identifier
   * @param json - JSON string of PersistedWizardState
   */
  async setShareWizardState(topicId: string, json: string): Promise<void> {
    try {
      await Preferences.set({ key: `share_wizard_${topicId}`, value: json });
    } catch (error) {
      if (isQuotaError(error)) throw error;
      // eslint-disable-next-line no-console
      console.error('Failed to set sharing wizard state:', error);
    }
  },

  /**
   * Clear share wizard leg state for a topic
   * @param topicId - Topic identifier
   */
  async clearShareWizardState(topicId: string): Promise<void> {
    try {
      await Preferences.remove({ key: `share_wizard_${topicId}` });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to clear sharing wizard state:', error);
    }
  },

  /**
   * Get saved template choices (tone + field values) for a topic
   * @param topicId - Topic identifier
   * @returns JSON string or null if not set
   */
  async getSharingTemplateChoices(topicId: string): Promise<string | null> {
    try {
      const { value } = await Preferences.get({
        key: `template_choices_${topicId}`,
      });
      return value ?? null;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to get template choices:', error);
      return null;
    }
  },

  /**
   * Save template choices for a topic
   * @param topicId - Topic identifier
   * @param json - JSON string of PersistedTemplateChoices
   */
  async setSharingTemplateChoices(
    topicId: string,
    json: string
  ): Promise<void> {
    try {
      await Preferences.set({
        key: `template_choices_${topicId}`,
        value: json,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to set template choices:', error);
    }
  },

  /**
   * Clear saved template choices for a topic
   * @param topicId - Topic identifier
   */
  async clearSharingTemplateChoices(topicId: string): Promise<void> {
    try {
      await Preferences.remove({ key: `template_choices_${topicId}` });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to clear template choices:', error);
    }
  },

  /**
   * Get the topic type last picked in the New Topic type picker on this device.
   * @returns The type id, or undefined if none has been picked
   */
  async getLastTopicType(): Promise<string | undefined> {
    try {
      const { value } = await Preferences.get({ key: KEYS.LAST_TOPIC_TYPE });
      return value ?? undefined;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to get last topic type:', error);
      return undefined;
    }
  },

  /**
   * Remember the topic type picked in the New Topic type picker, so it opens on
   * that type next time.
   */
  async setLastTopicType(typeId: string): Promise<void> {
    try {
      await Preferences.set({ key: KEYS.LAST_TOPIC_TYPE, value: typeId });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to set last topic type:', error);
    }
  },

  async getDeviceId(): Promise<string | undefined> {
    const { value } = await Preferences.get({ key: KEYS.DEVICE_ID });
    return value ?? undefined;
  },

  async setDeviceId(id: string): Promise<void> {
    await Preferences.set({ key: KEYS.DEVICE_ID, value: id });
  },

  async getSuppressedNotice(k: NoticeKey): Promise<boolean> {
    const { value } = await Preferences.get({
      key: SUPPRESSIBLE_NOTICE_KEYS[k],
    });
    return value === 'true';
  },

  async setNoticeSuppressed(k: NoticeKey): Promise<void> {
    await Preferences.set({ key: SUPPRESSIBLE_NOTICE_KEYS[k], value: 'true' });
  },

  async clearSuppressedNotice(k: NoticeKey): Promise<void> {
    await Preferences.remove({ key: SUPPRESSIBLE_NOTICE_KEYS[k] });
  },

  async clearAllSuppressedNotices(): Promise<void> {
    await Promise.all(
      Object.values(SUPPRESSIBLE_NOTICE_KEYS).map(k =>
        Preferences.remove({ key: k })
      )
    );
  },

  async getTabIds(): Promise<string[]> {
    try {
      const { value } = await Preferences.get({ key: KEYS.TAB_IDS });
      if (!value) return [];
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  },

  async setTabIds(ids: string[]): Promise<void> {
    try {
      await Preferences.set({
        key: KEYS.TAB_IDS,
        value: JSON.stringify(ids),
      });
    } catch {
      // Ignore storage errors
    }
  },

  async clearNonAddressBookData(): Promise<void> {
    const { keys } = await Preferences.keys();
    await Promise.all(
      keys
        .filter(k => !PRESERVE_ON_RESET.has(k))
        .map(k => Preferences.remove({ key: k }))
    );
  },
};
